import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { getBlockTypeMeta } from './flowBuilderUtils';

const ROLE_ICONS: Record<string, string> = {
  supervisor: '🧑‍💻',
  medical_analyst: '🏥',
  scheduler: '📅',
  gmail_agent: '✉️',
  log_agent: '📝',
  general_assistant: '🤖',
};

export const BlockNode = memo(({ data, selected }: NodeProps) => {
  const blockType = data.type as string;
  const meta = getBlockTypeMeta(blockType);
  const icon = meta?.icon || '⚡';
  const color = meta?.color || '#6b7280';
  const label = (data.label as string) || data.id || 'Untitled';
  const role = data.role as string | undefined;
  const roleIcon = role ? ROLE_ICONS[role] : '';
  const isEntry = data._isEntry as boolean | undefined;

  return (
    <div
      className={`relative rounded-lg border-2 shadow-sm bg-white dark:bg-claude-darkSurface transition-all min-w-[180px] max-w-[240px] ${
        selected
          ? 'border-claude-accent shadow-md ring-2 ring-claude-accent/30'
          : 'dark:border-claude-darkBorder border-claude-border'
      }`}
      style={{ borderTopColor: color, borderTopWidth: '3px' }}
    >
      <Handle type="target" position={Position.Left} className="!w-3 !h-3 !border-2" style={{ borderColor: color }} />

      <div className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium dark:text-claude-darkText text-claude-text truncate">
              {label}
            </div>
            <div className="text-[10px] uppercase tracking-wide dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {blockType}
              {role && ` · ${roleIcon} ${role}`}
            </div>
          </div>
        </div>

        {blockType === 'agent' && data.allowedMethods && (data.allowedMethods as string[]).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(data.allowedMethods as string[]).slice(0, 3).map((m: string) => (
              <span key={m} className="text-[9px] px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 truncate max-w-[80px]">
                {m}
              </span>
            ))}
            {(data.allowedMethods as string[]).length > 3 && (
              <span className="text-[9px] text-gray-400">+{(data.allowedMethods as string[]).length - 3}</span>
            )}
          </div>
        )}

        {blockType === 'function' && data.method && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary truncate">
            {data.method as string}
          </div>
        )}

        {blockType === 'router' && data.branches && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {Object.keys(data.branches as Record<string, string>).length} branches
          </div>
        )}

        {blockType === 'memory' && data.action && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {data.action as string}
          </div>
        )}

        {blockType === 'set' && data.values && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {Object.keys(data.values as Record<string, string>).length} keys
          </div>
        )}

        {blockType === 'code' && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary truncate">
            {(data.code as string)?.substring(0, 40) || '...'}
          </div>
        )}

        {blockType === 'compaction' && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
            threshold: {((data.threshold as number) ?? 0.75) * 100}%
          </div>
        )}

        {blockType === 'transform' && data.mapping && (
          <div className="mt-1 text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {Object.keys(data.mapping as Record<string, string>).length} mappings
          </div>
        )}
      </div>

      {isEntry && (
        <div className="absolute -top-2 -left-2 px-1 py-0.5 rounded text-[8px] font-bold bg-green-500 text-white shadow">
          START
        </div>
      )}

      <Handle type="source" position={Position.Right} className="!w-3 !h-3 !border-2" style={{ borderColor: color }} />
    </div>
  );
});

BlockNode.displayName = 'BlockNode';